import { namespace } from '@common-config/constants';
import { formatInLanguage } from '@common/config/moment-locales';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './calendar-grid.module.scss';
import { EventChip } from './event-chip';
import type { MonthLayout } from './layout/month-layout';
import { occurrenceKey } from './layout/occurrence-key';
import type { DragState } from './use-calendar-drag';
import type { CalendarLeanEvent } from './types';

interface Props {
    layout: MonthLayout;
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    now: number;
    drag: DragState | null;
    /** The cell under the pointer would refuse this drop. */
    dropRefused?: boolean;
    gridRef: React.RefObject<HTMLDivElement>;
    beginDrag: (event: CalendarLeanEvent, e: React.PointerEvent<HTMLElement>) => void;
    onOpenEvent: (event: CalendarLeanEvent, mouse: { x: number; y: number }) => void;
    onOpenDay: (date: Date) => void;
    /** Delete the ONE occurrence a chip stands for. */
    onDelete?: (event: CalendarLeanEvent) => void;
    onHoverStart?: (event: CalendarLeanEvent, rect: DOMRect) => void;
    onHoverEnd?: () => void;
    onCreateAt: (date: Date) => void;
}

export const MonthGrid: React.FC<Props> = ({
    layout, weekStartsOn, now, drag, dropRefused, gridRef, beginDrag,
    onOpenEvent, onHoverStart, onHoverEnd, onOpenDay, onCreateAt, onDelete,
}) => {
    const { t, i18n } = useTranslation(namespace);
    const weekdayLabels = useMemo(
        () => Array.from({ length: 7 }, (_, i) => moment().day((weekStartsOn + i) % 7).format('ddd')),
        [weekStartsOn],
    );

    // The month equivalent of the time grid's hover slot: an empty cell says, in
    // place, that a click creates a message here.
    const [hoverDayKey, setHoverDayKey] = useState<string | null>(null);

    const dropTargetKey = drag?.active && drag.targetMs != null
        ? moment(drag.targetMs).format('YYYY-MM-DD')
        : null;
    const draggingKey = drag?.active ? drag.key : null;

    // A drag captures the pointer on the chip, so the cell it leaves never gets
    // its pointerleave: without this the source day kept its "New message"
    // hint after the drop.
    const dragActive = !!drag?.active;
    useEffect(() => { if (dragActive) setHoverDayKey(null); }, [dragActive]);

    return (
        <>
            {/* The weekday header lives INSIDE the scroller, as a sticky row of the
                same grid as the cells — the same reason `time-grid.tsx` gives for
                its day header. As a sibling it was a second grid over a different
                width: `.monthBody` is a scroll container, so its seven columns are
                narrower than the header's by the scrollbar gutter. Sunday's line
                met, and the error accumulated to ~13px by Saturday. Same grid,
                same tracks, lines that actually meet. */}
            <div className={styles.monthBody} ref={gridRef}>
                <div className={styles.weekHead}>
                    {weekdayLabels.map(label => (
                        <div key={label} className={styles.weekHeadCell}>{label}</div>
                    ))}
                </div>

                {layout.weeks.flat().map((cell, cellIndex) => {
                    const key = moment(cell.date).format('YYYY-MM-DD');
                    return (
                        <div
                            key={key}
                            data-day={key}
                            className={[
                                styles.cell,
                                // The header is now the grid's first child, so the
                                // "last column" cells are no longer at nth-child(7n).
                                // Say which they are instead of counting siblings.
                                cellIndex % 7 === 6 ? styles.cellLastCol : '',
                                cell.inMonth ? '' : styles.cellOutside,
                                cell.isToday ? styles.cellToday : '',
                                dropTargetKey === key
                                    ? (dropRefused ? styles.cellDropRefused : styles.cellDropTarget)
                                    : '',
                            ].filter(Boolean).join(' ')}
                            onClick={e => { if (e.target === e.currentTarget) onCreateAt(cell.date); }}
                            onPointerEnter={() => setHoverDayKey(key)}
                            onPointerLeave={() => setHoverDayKey(current => (current === key ? null : current))}
                        >
                            <div className={styles.cellHead}>
                                {/* The date opens the day panel: the whole day, not just
                                    what fits in the cell — so the panel is reachable on a
                                    quiet day too, not only through an overflow row. */}
                                <button
                                    type="button"
                                    className={[styles.dayNum, cell.isToday ? styles.dayNumToday : ''].filter(Boolean).join(' ')}
                                    onClick={() => onOpenDay(cell.date)}
                                    aria-label={t('calendar.grid.showThisDay')}
                                >
                                    {cell.date.getDate()}
                                </button>
                                <button
                                    type="button"
                                    className={styles.quickAdd}
                                    // Read aloud, so it says the day the way the
                                    // user's language writes a date, not "2026-09-08".
                                    aria-label={t('calendar.grid.newMessageOn', {
                                        date: formatInLanguage(cell.date, i18n.language, 'LL'),
                                    })}
                                    onClick={() => onCreateAt(cell.date)}
                                >
                                    +
                                </button>
                            </div>

                            {cell.visible.map(event => (
                                <EventChip
                                    key={occurrenceKey(event)}
                                    event={event}
                                    now={now}
                                    size="month"
                                    isDragging={draggingKey === occurrenceKey(event)}
                                    onOpen={onOpenEvent}
                                    onDragStart={beginDrag}
                                onHoverStart={onHoverStart}
                                onHoverEnd={onHoverEnd}
                                onDelete={onDelete}
                                />
                            ))}

                            {!drag?.active && hoverDayKey === key && cell.overflow.length === 0 && (
                                <button
                                    type="button"
                                    className={styles.addHint}
                                    onClick={() => onCreateAt(cell.date)}
                                >
                                    {t('calendar.grid.newMessage')}
                                </button>
                            )}

                            {cell.overflow.length > 0 && (
                                <button type="button" className={styles.moreRow} onClick={() => onOpenDay(cell.date)}>
                                    {t('calendar.moreGroups', { count: cell.overflow.length })}
                                    <span className={styles.moreDots}>
                                        {cell.statusCounts.sent > 0 && <i className={styles.moreDot} style={{ background: 'var(--cal-ok-mark)' }} />}
                                        {cell.statusCounts.pending > 0 && <i className={styles.moreDot} style={{ background: 'var(--cal-warn-mark)' }} />}
                                        {cell.statusCounts.failed > 0 && <i className={styles.moreDot} style={{ background: 'var(--cal-bad-mark)' }} />}
                                    </span>
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};
